import React from 'react';
import Link from 'next/link';

import clsx from 'clsx';

import { CustomNextImage as Image } from '@crehana/next';

import { useGetCookie } from '@crehana/react-hooks';

import { useAmplitude } from '@crehana/analytics';
import { useTranslation } from '@crehana/i18n';
import { Container, Row, Col } from '@crehana/ui/dist/GridV2';
import PrimaryButton from '@crehana/ui/dist/Buttons/PrimaryButton';

import { AMPLITUDE_TRACKS } from '@/views/B2BLanding/events';

import data from './FreeTrialBanner.data';
import { FreeTrialBannerProps } from './types';
import {
  FreeTrialBannerWrapper,
  BannerWrapper,
} from './FreeTrialBanner.styles';

const FreeTrialBannerSection: React.FC<
  React.PropsWithChildren<FreeTrialBannerProps>
> = ({ title, description, btnText, withImage }) => {
  const { logEvent } = useAmplitude();
  const { t } = useTranslation();
  const { cookie: Variation } = useGetCookie('HOME_AB_TEST');

  return (
    <FreeTrialBannerWrapper className="py-52 sm:py-112 md:py-80">
      <Container>
        <Row>
          <Col offset={{ lg: 1 }} lg={10}>
            <BannerWrapper
              className={clsx(
                'bg-base-main text-white px-20 pt-24 flex flex-col md:relative sm:px-0 md:py-40',
                {
                  'pb-24': !withImage,
                },
              )}
            >
              <Row>
                {withImage && (
                  <Col className="order-2 sm:order-1" xs={12} sm={6}>
                    <div className="block w-full m-auto pt-20 md:w-auto md:absolute md:bottom-0 md:left-0 md:ml-20">
                      <Image
                        alt="Free Trial Banner"
                        src={data.image}
                        layout="fill"
                        objectPosition="center"
                        objectFit="cover"
                      />
                    </div>
                  </Col>
                )}
                <Col
                  className={clsx('order-1 sm:order-2', {
                    'pb-36 sm:pb-0': !withImage,
                  })}
                  offset={{
                    sm: withImage ? 0 : 1,
                  }}
                  sm={withImage ? 6 : 10}
                >
                  <div
                    className={clsx({
                      'flex flex-col sm:flex-row sm:items-center sm:justify-between':
                        !withImage,
                    })}
                  >
                    <div
                      className={clsx(
                        'pb-24',
                        { 'sm:w-7/12 sm:mr-8': !withImage },
                        {
                          'w-full sm:w-9/12': withImage,
                        },
                      )}
                    >
                      <h3 className="font-h3 mb-4 sm:mb-8 sm:w-8/12 md:w-auto">
                        {title || t('FREE_TRIAL_BANNER_TITLE')}
                      </h3>
                      <p className="font-body2">
                        {description || t('FREE_TRIAL_BANNER_DESCRIPTION')}
                      </p>
                    </div>
                    <Link
                      href="/business/prueba-gratis/"
                      passHref
                      legacyBehavior
                    >
                      <PrimaryButton
                        as="a"
                        href="/business/prueba-gratis/"
                        className="w-full sm:w-auto"
                        label={btnText || t('FREE_TRIAL_BANNER_BUTTON')}
                        onClick={() => {
                          logEvent({
                            event: AMPLITUDE_TRACKS.WANTS_TO_KNOW_MORE,
                            data: {
                              'CTA Type': 'button',
                              'CTA Location': 'free trial lower banner section',
                              'CTA Text': t('FREE_TRIAL_BANNER_BUTTON'),
                              Variation,
                            },
                          });
                        }}
                        darkMode
                        size="l"
                      />
                    </Link>
                  </div>
                </Col>
              </Row>
            </BannerWrapper>
          </Col>
        </Row>
      </Container>
    </FreeTrialBannerWrapper>
  );
};

export default FreeTrialBannerSection;
